'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense, useState } from 'react';
import { encodePassphrase, generateRoomId, randomString } from '@/lib/client-utils';
import styles from '../styles/Home.module.css';
import { setCookie, getCookie } from '../lib/cookieUtils';
import { useEffect } from 'react';
import Image from 'next/image';

function DemoMeetingTab(props: { label: string }) {
  const router = useRouter();
  const [e2ee, setE2ee] = useState(false);
  const [stt, setSTT] = useState(false);
  const [chatId, setChatId] = useState('');
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));
  const [meetingTopic, setMeetingTopic] = useState('');
  const [customTopic, setCustomTopic] = useState('');

  // Predefined topic options
  const topicOptions = [
    'обсуждение клиентов',
    'обсуждение продуктов',
    'обсуждение поставщиков',
    'обсуждение технологий',
    'обсуждение рынка',
    'обсуждение конкурентов',
    'обсуждение партнеров',
    'обсуждение сотрудников',
    'обсуждение совета директоров',
    'обсуждение акционеров',
    'обсуждение инвесторов',
    'обсуждение кредиторов',
    'Другое'
  ];

  useEffect(() => {
    const storedChatId = getCookie('chatId');
    const storedSTT = getCookie('sttEnabled');
    const storedTopic = getCookie('meetingTopic');

    if (storedChatId) {
      setChatId(storedChatId);
    }
    
    if (storedSTT === 'true') {
      setSTT(true);
    }

    if (storedTopic && storedSTT === 'true') {
      // Загружаем тему только если STT был включен
      if (topicOptions.includes(storedTopic)) {
        setMeetingTopic(storedTopic);
      } else {
        // Если тема не в списке, значит это кастомная тема
        setMeetingTopic('Другое');
        setCustomTopic(storedTopic);
      }
    }
  }, []);

  useEffect(() => {
    setCookie('chatId', chatId, 3650);
  }, [chatId]);

  useEffect(() => {
    setCookie('sttEnabled', stt.toString(), 3650);
    
    // Если STT отключен, сбрасываем выбранные темы
    if (!stt) {
      setMeetingTopic('');
      setCustomTopic('');
    }
  }, [stt]);

  useEffect(() => {
    const finalTopic = meetingTopic === 'Другое' ? customTopic : meetingTopic;
    setCookie('meetingTopic', finalTopic, 3650);
  }, [meetingTopic, customTopic]);

  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const handleVideoOpen = () => {
    setIsVideoOpen(true);
  };

  const handleVideoClose = () => {
    setIsVideoOpen(false);
  };

  const startMeeting = async () => {
    const roomId = generateRoomId();
    const passphrase = e2ee ? `#${encodePassphrase(sharedPassphrase)}` : '';
    
    // Always send meeting data to save room creation time and topic
    const finalTopic = meetingTopic === 'Другое' ? customTopic : meetingTopic;
    await fetch('/api/startMeeting', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        roomId, 
        chatId: stt ? chatId : null,
        language: 'auto',
        topic: finalTopic || 'Без темы'
      }),
    });
    
    router.push(`/rooms/${roomId}${passphrase}`);
  };

  return (
    <div className={styles.tabContent}>
      <p style={{ margin: 0 }}>Создавайте корпоративные встречи ХлебМаркет безлимитно и бесплатно.</p>
      
      {/* Meeting Topic Selection - только если включен STT */}
      {stt && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
        <label htmlFor="meeting-topic" style={{ fontWeight: 'bold' }}>Тема встречи:</label>
        <select
          id="meeting-topic"
          value={meetingTopic}
          onChange={(ev) => setMeetingTopic(ev.target.value)}
          style={{ 
            padding: '8px 12px', 
            borderRadius: '4px', 
            border: '1px solid #ccc',
            backgroundColor: 'white',
            fontSize: '14px',
            color: 'black'
          }}
        >
          <option value="">Выберите тему встречи...</option>
          {topicOptions.map((topic) => (
            <option key={topic} value={topic}>
              {topic}
            </option>
          ))}
        </select>
        {meetingTopic === 'Другое' && (
          <input
            type="text"
            placeholder="Введите свою тему..."
            value={customTopic}
            onChange={(ev) => setCustomTopic(ev.target.value)}
            style={{ 
              padding: '8px 12px', 
              borderRadius: '4px', 
              border: '1px solid #ccc',
              fontSize: '14px'
            }}
          />
        )}
      </div>
      )}

      <button style={{ marginTop: '1rem' }} className="lk-button" onClick={startMeeting}>
        Начать встречу
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
          <input
            id="use-e2ee"
            type="checkbox"
            checked={e2ee}
            onChange={(ev) => setE2ee(ev.target.checked)}
          ></input>
          <label htmlFor="use-e2ee">Enable end-to-end encryption</label>
        </div>
        {e2ee && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
            <label htmlFor="passphrase">Passphrase</label>
            <input
              id="passphrase"
              type="password"
              value={sharedPassphrase}
              onChange={(ev) => setSharedPassphrase(ev.target.value)}
            />
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
          <input
            id="use-stt"
            type="checkbox"
            checked={stt}
            onChange={(ev) => setSTT(ev.target.checked)}
          ></input>
          <label htmlFor="use-stt">Enable speech-to-text</label>
        </div>
        {stt && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
              <a href="https://t.me/meet_summary_bot" target='_blank'>Get cheat id</a>
              <div>
                <span
                  className="askico"
                  onClick={handleVideoOpen}
                >
                  ?
                </span>
                {isVideoOpen && (
                  <div
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      width: '100vw',
                      height: '100vh',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                    onClick={handleVideoClose}
                  >
                    <video
                      src="/images/chatinfo.mp4"
                      controls
                      autoPlay
                      style={{ maxWidth: '80%', maxHeight: '80%' }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
              <label htmlFor="use-stt-input">Set your telegram chat id</label>
              <input
                id="use-stt-input"
                type="text"
                value={chatId}
                onChange={(ev) => setChatId(ev.target.value)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Page() {
  const [logoSrc, setLogoSrc] = useState<string>('/images/hlebmarket-logo.png');

  useEffect(() => {
    const domain = window.location.hostname;

    const domainLogoMap: { [key: string]: string } = {
      'meet.soyuzxleb.ru': '/images/souzhleblogo.svg',
    }

    const newLogoSrc = domainLogoMap[domain] || '/images/hlebmarket-logo.png';
    setLogoSrc(newLogoSrc);

  }, []);
  
  return (
    <>
      <main className={styles.main} data-lk-theme="default">
        <div className="header">
          <Image 
            src="/images/souzhleblogo.svg" 
            alt="СоюзХлеб Конференц" 
            width={360} 
            height={74}
            priority
          />
          <Image 
            width={360} 
            height={60}
            style={{ padding: "20px 0 0 0" }} 
            src={logoSrc} 
            alt="Логотип ХлебМаркет" 
            priority
          />
          <h2>
            Создавайте и проводите корпоративные видеоконференции СоюзХлеб
          </h2>
        </div>
        <DemoMeetingTab label="Создать встречу" />
      </main>
    </>
  );
}
